import firmadokLoginImage from '@/assets/images/firmadok_login_image.jpg'
import firmadokLogo from '@/assets/images/MainLogo.svg'
import { Button, HideInProduction, Input } from '@/components'
import { Checkbox } from '@/components_deprecated'
import { Form } from '@/components_deprecated/Form'
import { useForm, useTranslate } from '@ur-solutions/react-hooks'
import { Link } from 'react-router'
import styled from 'styled-components'

import { HomescreenPrompt } from './HomescreenPrompt'
import SignInWithMicrosoft from './MicrosoftLogin/SignInWithMicrosoft'
import useLogin from './hooks/useLogin'

const Layout = styled.div`
  display: grid;
  grid-template-columns: 500px 1fr;
  height: 100%;
  min-height: 600px;

  ${props => props.theme.layout.mobile} {
    display: block;
  }
`

const LoginSection = styled.div`
  padding: 4rem 3rem;
  background-color: #f7f8fc;
  position: relative;

  height: 100%;
  box-sizing: border-box;

  ${props => props.theme.layout.mobile} {
    padding: 48px 24px;
  }
`

const ImageSection = styled.div`
  background: url(${firmadokLoginImage});
  background-size: cover;
  background-position: 50% 50%;

  ${props => props.theme.layout.mobile} {
    display: none;
  }
`

const TitleImage = styled.img`
  height: 50px;
`

const Subtitle = styled.h4`
  font-size: 24px;
  font-weight: normal;
  color: ${props => props.theme.colors.gray3};

  margin-top: 2rem;
  margin-bottom: 4rem;

  b {
    color: ${props => props.theme.colors.black};
    font-weight: 600;
  }

  ${props => props.theme.layout.mobile} {
    margin-bottom: 3rem;
  }
`

const FormFooter = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 1.5rem;
  margin-bottom: 1.5rem;
`

const Copyright = styled.div`
  position: absolute;
  bottom: 32px;
  left: 48px;
  font-size: 12px;
  color: ${props => props.theme.colors.gray6};
  ${props => props.theme.layout.mobile} {
    left: 24px;
  }
`

const ForgottenPasswordLink = styled(Link)`
  color: ${props => props.theme.colors.gray4};
  font-size: 12px;
  text-decoration: underline;
`

const LoginForm = styled(Form)`
  display: flex;
  flex-direction: column;
  gap: 1rem;

  button {
    align-self: center;
  }
`

export const Login: React.FC = () => {
  const translations = useTranslate({
    error: {
      wrongUserPassword: 'error.wrong-username-or-password',
    },
    forgotPassword: 'login.forgotten-password',
    form: {
      label: {
        username: 'login.email-or-phone-number',
        password: 'common.password',
        remember: 'login.remember-me',
      },
    },
    login: 'common.log-in',
    subtitle: 'login.subtitle',
    or: 'login.or',
  })

  const rememberMe: { rememberMe: boolean; username: string } = JSON.parse(
    localStorage.getItem('rememberMe') ?? '{}'
  )

  const {
    formValues: form,
    formErrors: errors,
    formChangeHandler: handler,
    submitHandler,
    updateFormErrors,
  } = useForm({
    values: {
      username: rememberMe?.username ?? '',
      password: '',
      rememberMe: rememberMe?.rememberMe ?? false,
    },
  })

  const { handleStandardLogin, loading } = useLogin({
    variant: 'standard',
    rememberMe: form.rememberMe,
    onErrorCallback: () =>
      updateFormErrors({ username: translations.error.wrongUserPassword }),
  })

  function handleSubmit() {
    if (loading) return
    void handleStandardLogin(form.username, form.password)
  }

  return (
    <Layout>
      <HomescreenPrompt />

      {/* TODO: Discuss same CompanyLogo as over sidebar here in future? */}
      <LoginSection>
        <TitleImage src={firmadokLogo} />

        <Subtitle>
          {translations.subtitle} <b>TaCtrl.ai</b>
        </Subtitle>

        <LoginForm preventDefault onSubmit={submitHandler(handleSubmit)}>
          <div>
            <Input
              className="w-full"
              id="username"
              name="username"
              value={form.username}
              label={translations.form.label.username}
              error={errors.username}
              autoFocus
              autoComplete="username"
              onFocus={() => updateFormErrors({ username: null })}
              onChange={handler('username')}
            />
          </div>

          <div>
            <Input
              className="w-full"
              id="password"
              name="password"
              type="password"
              value={form.password}
              label={translations.form.label.password}
              autoComplete="current-password"
              onFocus={() => updateFormErrors({ username: null })}
              onChange={handler('password')}
            />
          </div>

          <FormFooter>
            <Checkbox
              checked={form.rememberMe}
              label={translations.form.label.remember}
              tabIndex={3}
              onChange={handler('rememberMe')}
            />
            <ForgottenPasswordLink to="/forgotten-password" tabIndex={5}>
              {translations.forgotPassword}
            </ForgottenPasswordLink>
          </FormFooter>

          <Button type="submit" disabled={loading}>
            {translations.login}
          </Button>
        </LoginForm>

        <HideInProduction>
          <div className="text-center my-4 uppercase">{translations.or}</div>
          <div className="flex justify-center">
            <SignInWithMicrosoft />
          </div>
        </HideInProduction>
        <Copyright>&copy; TACtrl.ai AS, {new Date().getFullYear()}</Copyright>
      </LoginSection>

      <ImageSection />
    </Layout>
  )
}
